import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { SectionLabel } from "../components/site/SectionLabel";
import { CTASection } from "../components/site/CTASection";
import { getAdvisory, ADVISORY } from "../data/advisory";
import NotFound from "./NotFound";

export default function AdvisoryDetail() {
  const { slug } = useParams();
  const a = getAdvisory(slug);
  if (!a) return <NotFound />;
  const related = (a.related || []).map(getAdvisory).filter(Boolean);

  return (
    <div>
      <PageHero
        variant="dark"
        label="Advisory"
        title={a.title}
        intro={a.lead}
        crumbs={[{ label: "Advisory", to: "/advisory" }, { label: a.title }]}
      />

      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[2fr_1fr]">
          <div className="space-y-12">
            <section>
              <SectionLabel>What we cover</SectionLabel>
              <div className="grid gap-px border border-brand-line bg-brand-line sm:grid-cols-2">
                {a.topics.map((t) => (
                  <div key={t} className="flex items-start gap-3 bg-white p-5">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" />
                    <span className="text-[15px] text-brand-ink/85">{t}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <SectionLabel>The approach</SectionLabel>
              <div className="space-y-px border border-brand-line bg-brand-line">
                {a.approach.map((s, i) => (
                  <div key={i} className="grid gap-4 bg-white p-6 sm:grid-cols-[auto_1fr]">
                    <div className="font-mono text-2xl font-bold text-brand-blue">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-brand-ink">{s.h}</h3>
                      <p className="mt-1 text-[15px] leading-relaxed text-brand-muted">{s.p}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="space-y-8 lg:sticky lg:top-24 lg:self-start">
            <div className="border border-brand-blue/20 bg-brand-blue/5 p-6">
              <h3 className="font-heading text-lg font-bold text-brand-ink">Discuss this challenge</h3>
              <p className="mt-2 text-sm text-brand-muted">
                Tell us about your situation and we will suggest whether and how MATRIVON can help.
              </p>
              <Link
                to="/contact"
                data-testid="advisory-contact-cta"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 bg-brand-blue px-5 py-3 text-sm font-semibold text-white hover:bg-brand-bluedark"
              >
                Book a Conversation <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {related.length > 0 && (
              <div className="border border-brand-line bg-white p-6">
                <SectionLabel>Related advisory</SectionLabel>
                <ul className="space-y-3">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link
                        to={`/advisory/${r.slug}`}
                        className="group flex items-start gap-2 text-sm font-medium text-brand-ink hover:text-brand-blue"
                      >
                        <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" /> {r.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="border border-brand-line bg-white p-6">
              <SectionLabel>All advisory</SectionLabel>
              <ul className="space-y-2">
                {ADVISORY.map((r) => (
                  <li key={r.slug}>
                    <Link
                      to={`/advisory/${r.slug}`}
                      className={`text-sm ${r.slug === slug ? "font-semibold text-brand-blue" : "text-brand-muted hover:text-brand-ink"}`}
                    >
                      {r.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>

      <CTASection />
    </div>
  );
}
